import ReportService from '@/services/reports'

// Fuera del state: el id del timer y los contadores no le sirven a la vista y
// dentro del Proxy de Pinia solo dispararian renders. Hay un solo sondeo vivo.
let pollTimerId = null

// Una respuesta que estaba en vuelo cuando el usuario cerro el dialogo o pidio
// otra exportacion no debe escribir el state.
let pollGeneration = 0

// Dos startExport solapados (doble clic) pueden resolver en cualquier orden:
// la respuesta del mas viejo no debe pisar a la vigente.
let startGeneration = 0

// setTimeout recursivo y no setInterval: el siguiente sondeo se agenda cuando
// el anterior termino y no se encima si la red va lenta.
const POLL_INTERVAL_MS = 3000

// La exportacion tarda minutos y en el celular la red parpadea: un sondeo
// fallido no significa que el PDF haya fallado en el backend.
const MAX_CONSECUTIVE_POLL_FAILURES = 3

// Un job atascado en el backend solo lo marca el purge, entre 30 min y 3 h despues:
// pasado este tiempo se deja de seguir y la campanita avisa cuando termine.
// Con el reloj y no contando sondeos: en segundo plano el navegador frena los timers.
const MAX_POLL_DURATION_MS = 5 * 60 * 1000

const UNEXPECTED_ERROR = 'UNEXPECTED_ERROR'

// Se perdio el seguimiento, no la exportacion: el backend la sigue y avisa por la campanita.
const POLL_FAILED = 'POLL_FAILED'

function clearPollTimer () {
  if (pollTimerId) {
    clearTimeout(pollTimerId)
    pollTimerId = null
  }
}

// El 403 de la validacion y el 429 del throttle no traen errorCode: se deduce del status.
const ERROR_CODE_BY_STATUS = { 403: 'FORBIDDEN', 429: 'RATE_LIMITED' }

// responser.js rechaza con el AxiosResponse crudo: el codigo esta en error.data.
function readErrorCode (error) {
  return error?.data?.errorCode ?? ERROR_CODE_BY_STATUS[error?.status] ?? UNEXPECTED_ERROR
}

function readRetryAfterSeconds (error) {
  const seconds = Number.parseInt(error?.data?.retryAfterSeconds ?? error?.headers?.['retry-after'], 10)

  return Number.isFinite(seconds) ? seconds : null
}

export default {
  async startExport (query) {
    this.cancelPolling()
    const generation = ++startGeneration

    this.$reset()
    this.isStarting = true

    try {
      const { data } = await ReportService.startGeneralPdfExport(query)

      if (generation !== startGeneration) return false

      this.jobId = data.exportJobId
      this.status = data.status
      this.pollStatus()

      return true
    } catch (error) {
      console.error(error)
      if (generation !== startGeneration) return false

      this.status = 'failed'
      this.errorCode = readErrorCode(error)
      this.retryAfterSeconds = readRetryAfterSeconds(error)

      return false
    } finally {
      if (generation === startGeneration) this.isStarting = false
    }
  },

  // Para quien ya no tiene dialogo que siga la exportacion: solo la encola y no
  // toca el state ni sondea. Sin acuse, el backend avisa por la campanita.
  async enqueueExport (query) {
    try {
      await ReportService.startGeneralPdfExport(query)

      return true
    } catch (error) {
      console.error(error)

      return false
    }
  },

  pollStatus () {
    const generation = ++pollGeneration
    const deadline = Date.now() + MAX_POLL_DURATION_MS
    let consecutiveFailures = 0

    const tick = async () => {
      // El logout limpia el store con $reset sin pasar por cancelPolling.
      if (generation !== pollGeneration || !this.jobId) return

      try {
        const { data } = await ReportService.getReportExportStatus(this.jobId)
        if (generation !== pollGeneration) return

        consecutiveFailures = 0
        this.status = data.status
        this.downloadUrl = data.downloadUrl ?? null
        this.errorCode = data.errorCode ?? null

        if (data.status === 'succeeded' || data.status === 'failed') {
          clearPollTimer()

          return
        }
      } catch (error) {
        console.error(error)
        if (generation !== pollGeneration) return

        consecutiveFailures += 1
        if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
          this.status = 'failed'
          this.errorCode = POLL_FAILED
          clearPollTimer()

          return
        }
      }

      if (Date.now() >= deadline) {
        this.status = 'failed'
        this.errorCode = POLL_FAILED
        clearPollTimer()

        return
      }

      pollTimerId = setTimeout(tick, POLL_INTERVAL_MS)
    }

    // El PDF tarda al menos varios segundos: preguntar de inmediato solo gasta un request.
    pollTimerId = setTimeout(tick, POLL_INTERVAL_MS)
  },

  // Al cerrar el dialogo y al desmontarlo. No cancela la exportacion: el
  // backend la termina y avisa por la campanita, que es lo que se busca.
  // Tambien invalida un arranque en vuelo: su respuesta ya no se sigue.
  cancelPolling () {
    pollGeneration += 1
    startGeneration += 1
    this.isStarting = false
    clearPollTimer()
  },

  async downloadExportFile () {
    if (!this.downloadUrl) return null

    const { data, headers } = await ReportService.downloadReportExportFile(this.downloadUrl)

    return { data, headers }
  },

  // Best effort: si el acuse no llega, el backend manda la campanita y el
  // usuario recibe el archivo igual. Una notificacion de mas no amerita un error.
  async acknowledgeDownload () {
    if (!this.jobId) return

    try {
      await ReportService.ackReportExport(this.jobId)
    } catch (error) {
      console.error(error)
    }
  },

  async acknowledgeFailure () {
    if (!this.jobId) return

    try {
      await ReportService.ackReportExportFailure(this.jobId)
    } catch (error) {
      console.error(error)
    }
  },

  reset () {
    this.cancelPolling()
    this.$reset()
  }
}
